import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
// redux
import { selectToken } from "store/reducers/user/selectors";
// api
import {
  getQuery,
  postQuery,
  putQuery,
  urlStandingOrders,
} from "utils/requests";
import { getFirstAndLastDayOfMonth } from "utils/date";
// types
import {
  TPreview,
  TSaveResult,
  TStandingBody,
  TStandingOrder,
  TStatusDay,
} from "components/StandingOrder/types";

const errorMessage = (e: any): string =>
  (e &&
    e.response &&
    e.response.data &&
    (e.response.data.message ||
      (e.response.data.errors &&
        e.response.data.errors[0] &&
        e.response.data.errors[0].message))) ||
  "Something went wrong. Please try again.";

/**
 * Calls to /api/customer/standing-orders. The token is read when a call is
 * made (from a ref), not captured at first render, so a refreshed token is
 * always used. Every call except `isEnabled` rejects with an Error whose
 * message is the server's message.
 */
export const useStandingOrder = () => {
  const token = useSelector(selectToken);
  const tokenRef = useRef(token);
  tokenRef.current = token;

  const config = () => ({
    headers: { Authorization: `Bearer ${tokenRef.current}` },
  });

  const wrap = async <T>(run: () => Promise<{ data: T }>): Promise<T> => {
    try {
      return (await run()).data;
    } catch (e) {
      throw new Error(errorMessage(e));
    }
  };

  /** Fails closed: any problem means "not enabled". */
  const isEnabled = useCallback(async (): Promise<boolean> => {
    try {
      const res = await getQuery(`${urlStandingOrders}/enabled`, config());
      return !!(res && res.data && res.data.enabled === true);
    } catch (e) {
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getStanding = useCallback(
    (roomId: number | string) =>
      wrap<TStandingOrder>(() =>
        getQuery(`${urlStandingOrders}?roomId=${roomId}`, config())
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const previewStanding = useCallback(
    (roomId: number | string, body: TStandingBody) =>
      wrap<TPreview>(() =>
        postQuery(`${urlStandingOrders}/preview?roomId=${roomId}`, body, config())
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const saveStanding = useCallback(
    (roomId: number | string, body: TStandingBody) =>
      wrap<TSaveResult>(() =>
        putQuery(`${urlStandingOrders}?roomId=${roomId}`, body, config())
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const copyStanding = useCallback(
    (fromRoomId: number | string, toRoomId: number | string) =>
      wrap<{ toRoomId: number | string; version: number }>(() =>
        postQuery(
          `${urlStandingOrders}/copy`,
          { fromRoomId, toRoomId },
          config()
        )
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const getStatus = useCallback(
    (roomId: number | string, startDate: string, endDate: string) =>
      wrap<TStatusDay[]>(() =>
        getQuery(
          `${urlStandingOrders}/status?roomId=${roomId}&startDate=${startDate}&endDate=${endDate}`,
          config()
        )
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const revert = useCallback(
    (roomId: number | string, date: string, meal?: string) =>
      wrap<any>(() =>
        postQuery(
          `${urlStandingOrders}/revert`,
          // the daily screen holds the room id as a string from the URL
          meal
            ? { roomId: Number(roomId), date, meal }
            : { roomId: Number(roomId), date },
          config()
        )
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return {
    isEnabled,
    getStanding,
    previewStanding,
    saveStanding,
    copyStanding,
    getStatus,
    revert,
  };
};

/** null while unknown, then true/false. Fails closed. */
export const useStandingEnabled = (): boolean | null => {
  const { isEnabled } = useStandingOrder();
  const [enabled, setEnabled] = useState<boolean | null>(null);

  useEffect(() => {
    let alive = true;
    isEnabled().then((v) => alive && setEnabled(v));
    return () => {
      alive = false;
    };
  }, [isEnabled]);

  return enabled;
};

/** Standing status for the month holding `date`, for the calendar badges. */
export const useStandingMonth = (
  roomId: number | string | undefined,
  date: Date
) => {
  const enabled = useStandingEnabled();
  const { getStatus } = useStandingOrder();
  const [days, setDays] = useState<TStatusDay[]>([]);
  const time = date.getTime();

  useEffect(() => {
    if (!enabled || !roomId) {
      setDays([]);
      return;
    }
    let alive = true;
    const { startDay, endDay } = getFirstAndLastDayOfMonth(new Date(time));
    getStatus(roomId, startDay, endDay)
      .then((res) => alive && setDays(Array.isArray(res) ? res : []))
      .catch(() => alive && setDays([]));
    return () => {
      alive = false;
    };
  }, [enabled, roomId, time, getStatus]);

  return { enabled: enabled === true, days };
};

/**
 * Standing status for one day of the daily order screen, plus Go back.
 * `onReverted` runs after a revert so the screen reloads the order.
 */
export const useStandingDay = (
  roomId: string | null,
  menuDate: string | null,
  onReverted: () => void
) => {
  const enabled = useStandingEnabled();
  const { getStatus, revert } = useStandingOrder();
  const [day, setDay] = useState<TStatusDay | null>(null);
  const [error, setError] = useState<string | null>(null);

  const seq = useRef(0);

  const load = useCallback(() => {
    const mine = ++seq.current;
    if (!enabled || !roomId || !menuDate) {
      setDay(null);
      return Promise.resolve();
    }
    return getStatus(roomId, menuDate, menuDate)
      .then((res) => {
        if (mine !== seq.current || !mounted.current) return;
        setDay((Array.isArray(res) && res.find((d) => d.date === menuDate)) || null);
      })
      .catch(() => {
        if (mine === seq.current && mounted.current) setDay(null);
      });
  }, [enabled, roomId, menuDate, getStatus]);

  useEffect(() => {
    setDay(null);
    setError(null);
    load();
  }, [load]);

  // a response that lands after unmount must not set state
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const goBack = useCallback(
    async (meal?: string) => {
      if (!roomId || !menuDate) return;
      setError(null);
      try {
        await revert(roomId, menuDate, meal);
        await load();
        onReverted();
      } catch (e) {
        setError(e.message);
      }
    },
    [roomId, menuDate, revert, load, onReverted]
  );

  return { enabled: enabled === true, day, error, goBack };
};
